import { useTranslations } from "next-intl"

import { moneyShort } from "@/components/marketplace/format"
import { Heading, Text } from "@/components/ui"

interface PaymentTimelineProps {
  installmentAmount: number
  installments?: number
  intervalDays?: number
  label?: string
  body?: string
  productName?: string
}

// The financing claim, made by numbers instead of an icon grid. $0 is a stop on
// the track rather than a line above it, so the green stretch has two ends to
// run between — that stretch is the argument, and it means one thing only: the
// fifteen days the order is already at home before anything is charged.
export const PaymentTimeline = ({ installmentAmount, installments = 4, intervalDays = 15, label, body, productName }: PaymentTimelineProps) => {
  const t = useTranslations("marketplace.bnpl")
  const stops = [
    { amount: moneyShort(0), when: t("timelineToday"), today: true },
    ...Array.from({ length: installments }, (_, index) => ({
      amount: moneyShort(installmentAmount),
      when: t("timelineAfterDays", { days: intervalDays * (index + 1) }),
      today: false,
    })),
  ]
  const slot = 100 / stops.length

  return (
    <div className="rounded-20x bg-surface @container px-600 py-700 @3xl:px-1200 @3xl:py-1200">
      <div className="flex flex-col gap-300">
        <Heading as="h3" variant="xl" family="display" tone="strong" balance className="@3xl:text-display-lg max-w-title">
          {label ?? t("zeroInterestHeadline")}
        </Heading>
        {body && (
          <Text as="p" variant="bodyLg" family="text" tone="default" className="max-w-measure">
            {body}
          </Text>
        )}
      </div>

      <div className="border-line-subtle mt-700 border-t pt-700">
        {/* Five stops across a narrow column crush the amounts to 12px and wrap
            every caption to three lines. Below the spine's comfortable width the
            plan reads down instead. */}
        <ol className="flex flex-col @3xl:hidden">
          {stops.map((stop, index) => (
            <li key={stop.when} className="relative flex items-start gap-300 py-150 first:pt-0 last:pb-0">
              <span className="relative flex w-3 shrink-0 justify-center self-stretch" aria-hidden>
                {index > 0 && <span className="bg-track-dashed absolute top-0 bottom-1/2 w-px" />}
                {index < stops.length - 1 && <span className="bg-track-dashed absolute top-1/2 bottom-0 w-px" />}
                <span className={`absolute top-1/2 size-2.5 -translate-y-1/2 rounded-full ${stop.today ? "bg-fg-promise" : "border-brand bg-surface border-2"}`} />
              </span>
              <span className="flex min-w-0 flex-1 items-baseline justify-between gap-300">
                <Text as="span" variant="bodySm" family="display" tone="inherit" className={`leading-tight ${stop.today ? "text-fg font-bold" : "text-fg font-semibold"}`}>
                  {stop.when}
                </Text>
                <Text as="span" variant="body" family="display" tone="inherit" numeric className={`shrink-0 font-extrabold ${stop.today ? "text-fg-promise" : "text-fg"}`}>
                  {stop.amount}
                </Text>
              </span>
            </li>
          ))}
        </ol>

        <div aria-hidden className="relative hidden h-1.5 @3xl:block">
          <span className="bg-surface-muted absolute inset-x-0 top-0 h-1.5 rounded-full" />
          <span className="bg-fg-promise absolute top-0 h-1.5 rounded-full" style={{ left: `${slot / 2}%`, width: `${slot}%` }} />
          {stops.map((stop, index) => (
            <span
              key={stop.when}
              className={`absolute top-0 size-1.5 -translate-x-1/2 scale-250 rounded-full ${stop.today ? "bg-fg-promise" : "bg-brand"}`}
              style={{ left: `${slot / 2 + index * slot}%` }}
            />
          ))}
        </div>

        <ol className="mt-400 hidden gap-x-150 @3xl:grid" style={{ gridTemplateColumns: `repeat(${stops.length}, minmax(0, 1fr))` }}>
          {stops.map((stop) => (
            <li key={stop.when} className="flex flex-col items-center gap-100 text-center">
              <Text
                as="b"
                variant="bodySm"
                family="display"
                tone="inherit"
                numeric
                className={`@sm:text-heading-sm @lg:text-heading-lg @3xl:text-heading-xl ${stop.today ? "text-fg-promise" : "text-fg-strong"}`}
              >
                {stop.amount}
              </Text>
              <Text as="span" variant="caption" family="text" tone="muted" numeric className="@lg:text-body-sm">
                {stop.when}
              </Text>
            </li>
          ))}
        </ol>

        <Text as="p" variant="label" family="text" tone="promise" className="mt-600 hidden items-center gap-300 font-semibold @3xl:flex">
          <span aria-hidden className="bg-fg-promise h-1.5 w-6 shrink-0 rounded-full" />
          {t("timelineFreeStretch", { days: intervalDays })}
        </Text>
      </div>

      {productName && <p className="sr-only">{productName}</p>}
    </div>
  )
}
